import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Chip,
    TextField,
    Typography,
    Checkbox,
    Paper,
    CircularProgress,
    ListItemText,
    Tooltip,
    IconButton,
    InputAdornment,
    Autocomplete,
    FormHelperText
} from "@mui/material";
import { useGetTenanciesQuery } from "../store/services/tenancy.service";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import HelpIcon from "@mui/icons-material/HelpOutline";
import MainCard from "./cards/MainCard";

function TenancySelectionComponent({ selectedTenancies, onChange, disabled }) {
    const [tenancyOptions, setTenancyOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');

    const { data: tenanciesData, isLoading } = useGetTenanciesQuery({
        perPage: 5000,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    // Global option (ID 0) for sharing with all tenancies
    const globalOption = { tenancyId: 0, name: "All Tenancies" };

    useEffect(() => {
        if (tenanciesData) {
            // Add global option at the beginning
            setTenancyOptions([globalOption, ...tenanciesData]);
        }
    }, [tenanciesData]);

    // Convert selectedTenancies array of IDs to array of tenancy objects
    const selectedTenancyObjects = useMemo(() => {
        if (!tenancyOptions.length) return [];

        return selectedTenancies.map(id => {
            const found = tenancyOptions.find(t => t.tenancyId === id);
            return found || { tenancyId: id, name: `Tenancy ${id}` };
        });
    }, [selectedTenancies, tenancyOptions]);

    const handleDelete = (tenancyIdToDelete, event) => {
        // Stop event propagation to prevent the dropdown from opening
        if (event) {
            event.stopPropagation();
        }

        // Remove the selected tenancy from the list
        const newSelectedTenancies = selectedTenancies.filter(id => id !== tenancyIdToDelete);
        onChange(newSelectedTenancies);
    };

    const handleChange = (event, newValues) => {
        // Extract just the IDs from the tenancy objects
        const newValueIds = newValues.map(item => item.tenancyId);

        // If "All Tenancies" (ID 0) is selected, just use that
        if (newValueIds.includes(0)) {
            onChange([0]);
        } else {
            onChange(newValueIds);
        }
    };

    // Filter function for search
    const filterOptions = (options, { inputValue }) => {
        const searchText = inputValue.toLowerCase();
        return options.filter(
            option =>
                option.name.toLowerCase().includes(searchText) ||
                option.tenancyId.toString().includes(searchText)
        );
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 2 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography>Loading tenancies...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Share with Tenancies
                </Typography>
                <Tooltip title="Select tenancies to share this course with. Selecting 'All Tenancies' will share with everyone." placement="top">
                    <IconButton size="small" sx={{ ml: 1 }}>
                        <HelpIcon sx={{ color: "secondary.main" }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Autocomplete
                multiple
                id="tenancy-selection"
                options={tenancyOptions}
                value={selectedTenancyObjects}
                onChange={handleChange}
                getOptionLabel={(option) =>
                    option.tenancyId === 0 ?
                        option.name :
                        `[${option.tenancyId}] ${option.name}`
                }
                isOptionEqualToValue={(option, value) => option.tenancyId === value.tenancyId}
                filterOptions={filterOptions}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                disabled={disabled}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={selectedTenancies.length ? "" : "Search tenancies by ID or name"}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                </>
                            )
                        }}
                    />
                )}
                renderOption={(props, option, { selected }) => {
                    // Disable selection if "All Tenancies" is already selected
                    const isDisabled = selectedTenancies.includes(0) && option.tenancyId !== 0;

                    return (
                        <li
                            {...props}
                            style={{
                                opacity: isDisabled ? 0.5 : 1,
                                pointerEvents: isDisabled ? 'none' : 'auto'
                            }}
                        >
                            <Checkbox
                                checked={selected}
                                sx={{ mr: 1 }}
                            />
                            <ListItemText
                                primary={
                                    <Box component="span" sx={{ fontWeight: option.tenancyId === 0 ? 'bold' : 'normal' }}>
                                        {option.tenancyId === 0 ? (
                                            option.name
                                        ) : (
                                            <React.Fragment>
                                                <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                                                    [{option.tenancyId}]
                                                </Box>
                                                {option.name}
                                            </React.Fragment>
                                        )}
                                    </Box>
                                }
                            />
                        </li>
                    );
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            {...getTagProps({ index })}
                            key={option.tenancyId}
                            label={option.tenancyId === 0 ?
                                option.name :
                                `[${option.tenancyId}] ${option.name}`
                            }
                            color={option.tenancyId === 0 ? "primary" : "default"}
                            deleteIcon={
                                <CancelIcon
                                    onMouseDown={(event) => event.stopPropagation()}
                                />
                            }
                            onDelete={(event) => handleDelete(option.tenancyId, event)}
                        />
                    ))
                }
                ListboxProps={{
                    style: { maxHeight: 300 }
                }}
                PaperComponent={props => (
                    <Paper
                        {...props}
                        elevation={8}
                    />
                )}
            />
        </Box>
    );
}

TenancySelectionComponent.propTypes = {
    selectedTenancies: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

TenancySelectionComponent.defaultProps = {
    selectedTenancies: [],
    disabled: false
};

export default TenancySelectionComponent;